import { useAutoneTranslation } from '@autone/translations';
import {
  createColumnLoadingGuards,
  DataTableCellContent,
  DataTableCellSubtitle,
  DataTableCellTitle,
  DataTableHeaderCellWithSorting,
  type DataTableLoadingObject,
  TailwindProductCell,
  TailwindSkeleton,
} from '@autone/ui';
import { type ColumnHelper } from '@tanstack/react-table';

import { type APP_NAME } from '../../../../consts';
import { type AssortmentProductItem } from '../../../../redux/assortment';

const COLUMN_ID = 'product_name' as const;

export type ProductNameColumnId = typeof COLUMN_ID;

interface ProductNameFields {
  product: AssortmentProductItem['product'];
}

const { columnCellGuard, accessorFnGuard } =
  createColumnLoadingGuards<ProductNameFields>();

export const createProductNameColumn = <
  T extends ProductNameFields | DataTableLoadingObject,
>(
  columnHelper: ColumnHelper<T>,
) =>
  columnHelper.accessor(
    accessorFnGuard(
      (row) => row.product.style_desc + ' ' + row.product.style_id,
    ),
    {
      id: COLUMN_ID,
      size: 300,
      header: function ProductNameHeader({ column }) {
        const { t } = useAutoneTranslation<typeof APP_NAME>();

        return (
          <DataTableHeaderCellWithSorting
            column={column}
            arrowPosition="right"
            showSortable
          >
            <DataTableCellContent>
              <DataTableCellTitle className="uppercase font-medium">
                {t('table-columns.product')}
              </DataTableCellTitle>
              <DataTableCellSubtitle>
                {t('table-columns.product-id')}
              </DataTableCellSubtitle>
            </DataTableCellContent>
          </DataTableHeaderCellWithSorting>
        );
      },
      cell: (ctx) =>
        columnCellGuard({
          ctx,
          renderCell: function ProductNameCell({ row }) {
            const product = row.original.product;

            return (
              <TailwindProductCell
                productId={product.id}
                styleDesc={product.style_desc}
                imageUrl={product.image_url}
                colorDesc={product.color_desc}
                materialDesc={product.material_desc}
              />
            );
          },
          renderLoader: () => (
            <div className="flex flex-row gap-4 h-full w-full">
              <TailwindSkeleton className="h-full aspect-[3/2] rounded-sm" />
              <TailwindSkeleton className="flex-1 h-full rounded-sm" />
            </div>
          ),
        }),
    },
  );
